import { cardModeContext } from '@context/card-mode-context';
import { cardPreviewContext } from '@context/card-preview-context';
import { motion } from 'motion/react';
import { useContext } from 'react';

import headerStyles from '../CardHeader.module.scss';
import type { CardAppearance, ImageType, PlaceholderImageSize } from '../types';
import styles from './BannerPicture.module.scss';
import { TemplatePlaceholder } from './TemplatePlaceholder';
import type { CardImage } from '.cache/__types__';

interface BannerPictureProps {
  canLock: boolean;
  editorVersion: string | undefined;
  img?: Partial<CardImage> | null;
  appearance: CardAppearance;
  quality: 'high' | 'low' | undefined;
  type: ImageType;
  placeholderImageSize: PlaceholderImageSize;
  onImageLoad?: () => void;
}

export const BannerPicture: React.FC<React.PropsWithChildren<BannerPictureProps>> = ({
  canLock,
  editorVersion,
  img,
  appearance,
  quality,
  type,
  placeholderImageSize,
  onImageLoad,
}) => {
  const { hideEmptyAttributes } = useContext(cardPreviewContext);
  const { mode } = useContext(cardModeContext);

  const alt = type;

  if (!img?.edited_url && img?.value_provider && !hideEmptyAttributes) {
    return (
      <TemplatePlaceholder
        type={type}
        showText
        isOptional={img.is_optional}
        position="absolute"
        placeholderImageSize={placeholderImageSize}
        url={img?.edited_url}
        inCardHeader={true}
      />
    );
  }

  if (img?.edited_url) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={styles['banner-image-container']}
        data-image-type={type}
        data-is-locked={img?.is_locked}
        data-can-lock={canLock}
        data-edit-mode={Boolean(mode === 'edit')}
      >
        <img
          className={styles['banner-image']}
          alt={alt}
          src={img.edited_url}
          data-appearance={appearance}
          data-image-type={type}
          data-edit-mode={Boolean(mode === 'edit')}
          data-editor-version={editorVersion || img.metadata?.editor_version || 'none'}
          data-img-quality={quality}
          onLoad={onImageLoad}
        />
      </motion.div>
    );
  }

  return <div className={headerStyles['circle-container']} />;
};
